import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import CustomValidators from '../../../../shared/custom-validators';
import { CustomerCampaignService } from '../../../../services/customer-campaign.service';
import { KeywordConfig } from '../../../../shared/models/keywords/keywords';
import Utils from '../../../../shared/utils';
import { map } from 'rxjs';
import { PermissionService } from '../../../../services/permission.service';
import Permissions from 'app/models/Permissions';

@Component({
  selector: 'app-components-shared-add-keyword',
  templateUrl: './add-keyword.component.html'
})
export class AddKeywordComponent implements OnInit {
  @Input() websiteUrl?: Boo.Objects.WebsiteUrl;
  @Input() customer: Boo.Objects.Customer;
  @Input() websiteUrls: Boo.Objects.WebsiteUrl[];
  @Input() trackingKeywords: Boo.Objects.WebsiteKeywordTracking[];
  @Input() archivedKeywords: Boo.Objects.ArchivedKeyword[];
  @Input() restrictions: Boo.Objects.CustomerCampaign.Enums.RestrictionTypes[];
  @Input() keywordConfig: KeywordConfig;

  @ViewChild('keywordInput') keywordInput: ElementRef;
  @ViewChild('areaInput') areaInput: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;

  keywordControl: UntypedFormControl;
  areaControl: UntypedFormControl;
  formGroup: UntypedFormGroup;
  addingKeyword = false;
  isAreaFirst = false;
  canImportInitialRank = false;
  defaultMaximumTrackingKeywords = 100;
  currentFileImportMode = KeywordFileImportMode.None;

  localCampaignCsvFormat = this.customerCampaignService.localCampaignCsvFormat;
  localCampaignExampleCsv = this.customerCampaignService.localCampaignExampleCsv;
  nationalCampaignCsvFormat = this.customerCampaignService.nationalCampaignCsvFormat;
  nationalCampaignExampleCsv = this.customerCampaignService.nationalCampaignExampleCsv;
  localCampaignInitialRankCsvFormat = this.customerCampaignService.localCampaignInitialRankCsvFormat;
  localCampaignInitialRankExampleCsv = this.customerCampaignService.localCampaignInitialRankExampleCsv;
  nationalCampaignInitialRankCsvFormat = this.customerCampaignService.nationalCampaignInitialRankCsvFormat;
  nationalCampaignInitialRankExampleCsv = this.customerCampaignService.nationalCampaignInitialRankExampleCsv;

  customerCampaignRestrictionTypes_ReadOnly = Boo.Objects.CustomerCampaign.Enums.RestrictionTypes.ReadOnly;
  fileImport_Standard = KeywordFileImportMode.Standard;
  fileImport_WithInitialRank = KeywordFileImportMode.WithInitialRank;

  constructor(
    private customerCampaignService: CustomerCampaignService,
    private permissionService: PermissionService) { }

  ngOnInit(): void {
    this.setupForm();

    if (!this.websiteUrl) {
      this.permissionService.hasPermission(Permissions.CanImportKeywordInitialRanks)
        .subscribe(canImport => this.canImportInitialRank = canImport);
    }
  }

  toggleAddingKeyword(): void {
    this.addingKeyword = !this.addingKeyword;

    if (this.addingKeyword) {
      this.isAreaFirst = false;
      this.keywordControl.reset('');
      this.areaControl.reset('');
      this.focus();
    }
  }

  toggleAreaPosition(): void {
    this.isAreaFirst = !this.isAreaFirst;
    this.focus();
  }

  setupForm(): void {
    this.keywordControl = new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(128),
      CustomValidators.mustNotBeWhitespace()
    ]);
    this.areaControl = new UntypedFormControl('', [
      CustomValidators.requiredIfValidator(() => !this.customer.IsCampaignNational),
      Validators.maxLength(256),
      CustomValidators.mustNotBeWhitespace()
    ]);
    this.formGroup = new UntypedFormGroup({ keyword: this.keywordControl, area: this.areaControl }, {
      validators: [CustomValidators.keywordsCannotBeDuplicated(this.customerCampaignService, () => this.isAreaFirst, this.getExistingKeywordPhrases.bind(this), 'This keyword already exists and cannot be duplicated.')]
    });
    this.focus();
  }

  getExistingKeywordPhrases(): string[] {
    return this.websiteUrls.filter(x => x.StatusId !== Boo.Objects.Enums.WebsiteUrlStatuses.Archived)
      .flatMap(x => x.Keywords)
      .filter(x => x.IsActive)
      .map(x => x.KeywordPhrase.toLowerCase().trim())
      .concat(
        this.trackingKeywords.filter(x => x.IsActive).map(x => x.KeywordPhrase.toLowerCase().trim())
      );
  }

  addKeyword(): void {
    this.keywordControl.markAsDirty();
    this.keywordControl.updateValueAndValidity();

    if (!this.customer.IsCampaignNational) {
      this.areaControl.markAsDirty();
      this.areaControl.updateValueAndValidity();
    } else {
      this.areaControl.reset('');
      this.isAreaFirst = false;
    }

    if (!this.formGroup.valid) {
      toastr.error("Please correct errors before adding a keyword.");
      return;
    }

    const phrase = this.customerCampaignService.getKeywordPhrase(this.keywordControl.value, this.areaControl.value, this.isAreaFirst);

    if (this.websiteUrl) {
      this.addWebsiteUrlKeyword(phrase);
    } else {
      this.addTrackingKeyword(phrase);
    }

    const archivedKeyword = this.archivedKeywords.find(x => x.KeywordPhrase.toLowerCase().trim() === phrase.toLowerCase());
    if (archivedKeyword) {
      this.archivedKeywords.splice(this.archivedKeywords.indexOf(archivedKeyword), 1);
    }

    this.toggleAddingKeyword();
  }

  openFileImport(importMode: KeywordFileImportMode): void {
    this.currentFileImportMode = importMode;
    this.fileInput.nativeElement.click();
  }

  importTrackingKeywords(event: any): void {
    const file: File = event.target.files[0];
    this.fileInput.nativeElement.value = null;

    switch (this.currentFileImportMode) {
      case KeywordFileImportMode.Standard:
        this.importTrackingKeywordsWithoutRank(file);
        break;
      case KeywordFileImportMode.WithInitialRank:
        this.importTrackingKeywordsWithRank(file);
        break;
    }

    this.currentFileImportMode = KeywordFileImportMode.None;
  }
  
  importTrackingKeywordsWithoutRank(file: File) {
    if (file) {
      const expectedCsvFormat = this.customer.IsCampaignNational ? this.nationalCampaignCsvFormat : this.localCampaignCsvFormat;
      Utils.parseCsv(file, expectedCsvFormat).pipe(map(results => results.map(row => { 
        const keyword = this.customerCampaignService.normalizeKeyword(row[0]);
        const area = row[1] ? this.customerCampaignService.normalizeArea(row[1]) : '';
        return {
          keyword: keyword,
          area: area,
          phrase: this.customerCampaignService.getKeywordPhrase(keyword, area, this.isAreaFirst)
        } as TrackingKeywordImport;
      }))).subscribe({
        next: (importValues) => {
          this.importUpdateTrackingKeywords(importValues);
        },
        error: (err: any) => toastr.error(err.message)
      });
    }
  }

  importTrackingKeywordsWithRank(file: File) {
    if (file) {
      const expectedCsvFormat = this.customer.IsCampaignNational ? this.nationalCampaignInitialRankCsvFormat : this.localCampaignInitialRankCsvFormat;
      Utils.parseCsv(file, expectedCsvFormat).pipe(map(results => results.map((row, index)  => {
        const keyword = this.customerCampaignService.normalizeKeyword(row[0]);
        const area = this.customer.IsCampaignNational ? '' : this.customerCampaignService.normalizeArea(row[1]);
        const initialRank = parseInt(row[this.customer.IsCampaignNational ? 1 : 2]);
        const initialRankDate = new Date(row[this.customer.IsCampaignNational ? 2 : 3]);
        this.validateRank(initialRank, index + 2); // +2 to account for header row and 0-based index
        this.validateDate(initialRankDate, index + 2);
        return {
          keyword: keyword,
          area: area,
          phrase: this.customerCampaignService.getKeywordPhrase(keyword, area, this.isAreaFirst),
          initialRank: parseInt(row[this.customer.IsCampaignNational ? 1 : 2]),
          initialRankDate: new Date(row[this.customer.IsCampaignNational ? 2 : 3])
        } as TrackingKeywordImport;
      }))).subscribe({
        next: (importValues) => {
          this.importUpdateTrackingKeywords(importValues);
        },
        error: (err: any) => toastr.error(err.message)
      });
    }
  }

  exportTrackingKeywordsWithInitialRank(): void {
    const header = this.customer.IsCampaignNational ? this.nationalCampaignInitialRankCsvFormat.join(',') : this.localCampaignInitialRankCsvFormat.join(',');
    const csvString = header + '\n' + this.trackingKeywords
      .filter(x => x.IsActive)
      .map(x => '"' + x.Keyword.Name + (this.customer.IsCampaignNational ? '' : '","' + (x.Area?.Name ?? '')) + '",' + (x.InitialRank ?? '') + ',' + (x.InitialRankDate ?? ''))
      .join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;', endings: 'native' });
    const fileName = this.customer.IsCampaignNational ? 'NationalTrackingKeywordsWithInitialRank.csv' : 'LocalTrackingKeywordsWithInitialRank.csv';
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', fileName);
    element.click();
    element.remove();
  }

  // This currently only counts tracking keywords to disable adding more than the maximum
  get maximumKeywordsAdded(): boolean {
    return !this.websiteUrl && this.trackingKeywords.filter(x => x.IsActive && !x.WasFirstPage).length >= (this.keywordConfig?.MaximumTrackingKeywords || this.defaultMaximumTrackingKeywords);
  }

  private importUpdateTrackingKeywords(importValues: TrackingKeywordImport[]): void {
    const maximumTrackingKeywords = this.keywordConfig?.MaximumTrackingKeywords || this.defaultMaximumTrackingKeywords;
    const existingTrackingKeywordTotal = this.trackingKeywords.filter(x => x.IsActive && !x.WasFirstPage).length;
    let currentTrackingKeywordTotal = existingTrackingKeywordTotal;
    const uploadedTotal = importValues.length;
    const uniqueUploadedValues = Array.from(new Map(importValues.map(item => [item.phrase, item])).values());
    const updatedTotal = uniqueUploadedValues.filter(x => this.trackingKeywords.some(
      y => y.IsActive && y.KeywordPhrase.toLowerCase() === x.phrase.toLowerCase()
        && ((!!x.initialRank && x.initialRank !== y.InitialRank) || (!!x.initialRankDate && x.initialRankDate.getTime() !== y.InitialRankDate.getTime())))).length;

    for (const value of uniqueUploadedValues) {
      if (currentTrackingKeywordTotal >= maximumTrackingKeywords) {
        break;
      }
      
      this.addImportedTrackingKeyword(value.keyword, value.area, this.isAreaFirst, value.initialRank, value.initialRankDate);
      currentTrackingKeywordTotal = this.trackingKeywords.filter(x => x.IsActive && !x.WasFirstPage).length;
    }

    const importedTotal = currentTrackingKeywordTotal - existingTrackingKeywordTotal;

    if (importedTotal === uploadedTotal) {
      toastr.success(`All ${ importedTotal } keywords imported.`);
    } else if (importedTotal + updatedTotal === uploadedTotal) {
      toastr.success(`${ importedTotal } keywords imported.<br />${ updatedTotal } keywords updated.`);
    } else {
      toastr.warning(`${ importedTotal }/${ uploadedTotal } keywords imported.<br />`
        + (updatedTotal > 0 ? `${ updatedTotal } keywords updated.<br />` : '')
        + `${ uploadedTotal - importedTotal - updatedTotal } keywords omitted due to duplicates or the tracking keyword maximum.`);
    }
  }

  private addImportedTrackingKeyword(keyword: string, area: string, isAreaFirst: boolean, initialRank?: number, initialRankDate?: Date): void {
    const phrase = this.customerCampaignService.getKeywordPhrase(keyword, area, isAreaFirst);
    const existingKeyword = this.trackingKeywords.find(x => x.KeywordPhrase.toLowerCase().trim() === phrase.toLowerCase());
    if (existingKeyword) {
      existingKeyword.IsActive = true;
      existingKeyword.IsModified = true;
      existingKeyword.InitialRank = initialRank ? initialRank : existingKeyword.InitialRank;
      existingKeyword.InitialRankDate = initialRankDate ? initialRankDate : existingKeyword.InitialRankDate;
    } else {
      this.trackingKeywords.push({
        Area: area ? { AreaId: 0, Name: this.customerCampaignService.normalizeArea(area) } as Boo.Objects.Area : null,
        AreaId: 0,
        CustomerId: this.customer.CustomerId,
        IsActive: true,
        IsAreaFirst: isAreaFirst,
        Keyword: { KeywordId: 0, Name: this.customerCampaignService.normalizeKeyword(keyword) } as Boo.Objects.Keyword,
        KeywordId: 0,
        KeywordPhrase: phrase,
        IsModified: true,
        InitialRank: initialRank,
        InitialRankDate: initialRankDate
      } as Boo.Objects.WebsiteKeywordTracking);
    }
  }

  private addTrackingKeyword(phrase: string): void {
    const existingKeyword = this.trackingKeywords.find(x => x.KeywordPhrase.toLowerCase().trim() === phrase.toLowerCase());
    if (existingKeyword) {
      existingKeyword.IsActive = true;
      existingKeyword.IsModified = true;
    } else {
      this.trackingKeywords.push({
        Area: this.areaControl.value ? { AreaId: 0, Name: this.customerCampaignService.normalizeArea(this.areaControl.value) } as Boo.Objects.Area : null,
        AreaId: 0,
        CustomerId: this.customer.CustomerId,
        IsActive: true,
        IsAreaFirst: this.isAreaFirst,
        Keyword: { KeywordId: 0, Name: this.customerCampaignService.normalizeKeyword(this.keywordControl.value) } as Boo.Objects.Keyword,
        KeywordId: 0,
        KeywordPhrase: phrase,
        IsModified: true
      } as Boo.Objects.WebsiteKeywordTracking);
    }
  }

  private addWebsiteUrlKeyword(phrase: string): void {
    const existingKeyword = this.websiteUrl.Keywords.find(x => x.KeywordPhrase.toLowerCase().trim() === phrase.toLowerCase());
    if (existingKeyword) {
      existingKeyword.IsActive = true;
      existingKeyword.IsModified = true;
    } else {
      this.websiteUrl.Keywords.push({
        Area: this.areaControl.value ? { AreaId: 0, Name: this.customerCampaignService.normalizeArea(this.areaControl.value) } as Boo.Objects.Area : null,
        AreaId: 0,
        IsActive: true,
        IsAreaFirst: this.isAreaFirst,
        IsRecommended: false,
        KeywordId: 0,
        Keyword: { KeywordId: 0, Name: this.customerCampaignService.normalizeKeyword(this.keywordControl.value) } as Boo.Objects.Keyword,
        KeywordPhrase: phrase,
        WebsiteUrlId: this.websiteUrl.WebsiteUrlId,
        IsModified: true
      } as Boo.Objects.WebsiteUrlKeyword);
    }
  }

  private validateRank(rank: number, lineNumber: number): void {
    if (isNaN(rank)) {
      throw new Error(`Initial Rank on line ${lineNumber} is not a valid number.`);
    } else if (rank < 1 || rank > 101) {
      throw new Error(`Initial Rank on line ${lineNumber} must be between 1 and 101.`);
    }
  }

  private validateDate(date: Date, lineNumber: number): void {
    if (isNaN(date.getTime())) {
      throw new Error(`Initial Rank Date on line ${lineNumber} is not a valid date.`);
    }
  }

  private focus() {
    setTimeout(() => {
      if (this.isAreaFirst && this.areaInput) {
        this.areaInput.nativeElement.focus();
      } else if (this.keywordInput) {
        this.keywordInput.nativeElement.focus();
      }
    }, 0);
  }
}

interface TrackingKeywordImport {
  keyword: string;
  area?: string;
  phrase: string;
  initialRank?: number;
  initialRankDate?: Date;
}

enum KeywordFileImportMode {
  None,
  Standard,
  WithInitialRank
}